import { useRef, useState } from 'react';

import { Box, Col } from '@opendoor/atoms/box';
import { Text } from '@opendoor/atoms/text';
import { cn } from '@opendoor/atoms/utils';
import { IconCheck, IconChevronRight } from '@tabler/icons-react';
import type { EntryComponent } from 'cms/entries/entries';

import type { ILpComponentList, ILpComponentListItem } from '../../declarations/contentful';
import Container, { type ContainerBackgroundColor } from './shared/Container';
import Sheet from './shared/Sheet';
import { BNColorThemes, getBNComponentThemeColors } from './shared/styles/ComponentTheme';
import type { IBNComponentThemeConfig } from './shared/styles/ComponentTheme';
import { Desc, Eyebrow, TitleWithAccent } from './shared/Typography';
import { markdownToReactElement } from './shared/utils';

// The shared theme helper still returns Bricks color tokens (it's consumed by
// Bricks Typography components elsewhere). Map the tokens this component uses to
// Atoms semantic utilities. The class strings are literals so Tailwind generates
// them even though the lookup is dynamic.
const BG_CLASS: Record<string, string> = {
  $backgroundPrimary: 'bg-od-bg-primary',
  $backgroundInversePrimary: 'bg-od-bg-inverse-primary',
  $backgroundTertiary: 'bg-od-bg-tertiary',
};
const TEXT_CLASS: Record<string, string> = {
  $contentPrimary: 'text-od-content-primary',
  $contentInversePrimary: 'text-od-content-inverse-primary',
  $contentStateInactivePrimary: 'text-od-content-inactive-primary',
};

const ListItemIcon = ({
  isNumbered,
  hasCheckmark,
  isLight,
  index,
  iconBgClass,
  iconColorClass,
}: {
  isNumbered: boolean;
  hasCheckmark: boolean;
  isLight: boolean;
  index: number;
  iconBgClass: string;
  iconColorClass: string;
}) => {
  if (!isNumbered && !hasCheckmark) return null;

  return (
    <Box
      className={cn(
        'mt-0.5 shrink-0 items-center justify-center rounded-sm',
        isLight ? 'h-10 w-10 min-h-10 min-w-10' : 'h-6 w-6 min-h-6 min-w-6',
        iconBgClass,
      )}
    >
      {isNumbered ? (
        <Text as="span" className={cn('text-body-lg font-medium', iconColorClass)}>
          {index + 1}
        </Text>
      ) : (
        <IconCheck size={16} className={iconColorClass} strokeWidth={2} aria-hidden="true" />
      )}
    </Box>
  );
};

interface ItemDetailProps {
  item: ILpComponentListItem;
  theme: IBNComponentThemeConfig;
  listItemTitleSize: '18px' | '24px';
}

const ItemDetails = ({ item, theme = {} as IBNComponentThemeConfig }: ItemDetailProps) => {
  return (
    <Col className={cn('relative min-w-0 shrink gap-2', item.fields.url ? 'pr-5' : 'pr-0')}>
      {item.fields.eyebrow && (
        <Eyebrow mt="$2" color={theme.contentPrimary}>
          {item.fields.eyebrow}
        </Eyebrow>
      )}
      <Text as="p" className={cn('text-subheader-sm', TEXT_CLASS[theme.contentPrimary as string])}>
        {item.fields.title}
      </Text>
      {item.fields.desc && (
        <Text
          as="p"
          className={cn('block text-body-lg', TEXT_CLASS[theme.contentPrimary as string])}
        >
          {markdownToReactElement(item.fields.desc)}
        </Text>
      )}
      {(item.fields.url || item.fields.ctaSheet) && !item.fields.hideArrow && (
        <Box className="absolute top-1/2 left-full -mt-2">
          <IconChevronRight
            size={16}
            className="text-od-content-tertiary"
            strokeWidth={2}
            aria-hidden="true"
          />
        </Box>
      )}
    </Col>
  );
};

interface ItemSheetProps {
  item: ILpComponentListItem;
  theme: IBNComponentThemeConfig;
  listItemTitleSize: '18px' | '24px';
  'aria-label'?: string;
  analyticsName?: string;
}

const ItemSheet = ({ item, listItemTitleSize, theme, ...props }: ItemSheetProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const ctaRef = useRef<HTMLElement>(null);
  const exitComplete = () => {
    ctaRef.current?.focus();
  };

  return (
    <Col className="min-w-0 shrink">
      <Box
        ref={ctaRef}
        onClick={() => setIsVisible(true)}
        aria-label={props['aria-label'] || 'Open dialog'}
        analyticsName={props.analyticsName || ''}
        data-group
        className="items-center justify-start text-left"
      >
        <ItemDetails item={item} theme={theme} listItemTitleSize={listItemTitleSize} />
      </Box>
      <Sheet
        entry={item.fields.ctaSheet}
        isVisible={isVisible}
        setIsVisible={setIsVisible}
        exitComplete={exitComplete}
      />
    </Col>
  );
};

const RenderList = (entry: ILpComponentList) => {
  const { fields } = entry;

  const listBgColor =
    fields.listItemBg in BNColorThemes ? (fields.listItemBg as BNColorThemes) : BNColorThemes.Light;

  const layoutBgColor = getBNComponentThemeColors(
    fields.bgColor && fields.bgColor in BNColorThemes
      ? (fields.bgColor as BNColorThemes)
      : BNColorThemes.Light,
  );

  const theme = getBNComponentThemeColors(listBgColor);

  // List Item Icon Options
  const isNumbered = fields.listItemIcon === 'Number';
  const hasCheckmark = fields.listItemIcon === 'Checkmark';
  const isLight = listBgColor === BNColorThemes.Light;
  const iconBgClass = isLight ? 'bg-od-bg-tertiary' : 'bg-transparent';
  const iconColorClass = isLight
    ? 'text-od-content-primary'
    : TEXT_CLASS[theme.contentPrimary as string];

  // List Layout Options
  const headerAbove =
    fields.listLayout === 'Header Above' ||
    fields.listLayout === 'Header Above with Two Column List';
  const twoColumnList =
    fields.listLayout === 'Header Above with Two Column List' ||
    fields.listLayout === 'Header Left with Two Column List';
  const listItemTitleSize = headerAbove ? '18px' : '24px';

  return (
    <Container backgroundColor={layoutBgColor.bgPrimary as ContainerBackgroundColor}>
      <Col className={cn('gap-4', !headerAbove && 'md:flex-row')}>
        <Col className="mb-5 flex-[4] gap-4 pr-5 md:basis-full">
          {fields.title && (
            <TitleWithAccent
              overRideTitleTag={fields.titleTag as 'h1' | 'h2' | 'h3'}
              title={fields.title}
              titleAccent={fields.titleAccent}
              titleSize={fields.titleSize}
              $largerThanSM={{ typography: '$headerLarge' }}
              color={layoutBgColor.contentPrimary}
            />
          )}
          <Box className={cn('block', headerAbove ? 'md:max-w-[60ch]' : 'md:max-w-full')}>
            {fields.subhead && (
              <Text
                as="p"
                className={cn(
                  'block font-medium',
                  fields.titleSize === 'Large'
                    ? 'text-subheader-xs md:text-subheader-sm'
                    : 'text-label-md',
                  TEXT_CLASS[layoutBgColor.inactivePrimary as string],
                )}
              >
                {fields.subhead}
              </Text>
            )}
            {fields.desc && <Desc>{fields.desc}</Desc>}
          </Box>
        </Col>
        <Box className="block flex-[6] md:basis-full">
          <Col
            role="list"
            className={cn(
              'm-0 list-none p-0',
              isLight ? 'gap-0' : 'gap-2',
              twoColumnList && 'md:flex-row md:flex-wrap',
            )}
          >
            {fields.listItems?.map((item, idx) => (
              <Box
                key={idx}
                role="listitem"
                className={cn(
                  'flex-row items-center gap-5',
                  BG_CLASS[theme.bgPrimary as string],
                  twoColumnList && 'md:basis-[47%]',
                  isLight ? 'border-b border-od-border-primary py-6' : 'rounded-2xl py-7 pr-7 pl-6',
                )}
              >
                <ListItemIcon
                  isNumbered={isNumbered}
                  hasCheckmark={hasCheckmark}
                  isLight={isLight}
                  index={idx}
                  iconBgClass={iconBgClass}
                  iconColorClass={iconColorClass}
                />
                {/* eslint-disable-next-line no-nested-ternary -- Grandfathered violation */}
                {item.fields.url ? (
                  <Box
                    href={item.fields.url}
                    aria-label={item.fields.title || 'List item'}
                    analyticsName={`cosmos-landing-page-list-item-${item.fields?.analyticsId}`}
                    data-group
                    className="min-w-0 shrink no-underline"
                  >
                    <ItemDetails item={item} theme={theme} listItemTitleSize={listItemTitleSize} />
                  </Box>
                ) : item.fields.ctaSheet ? (
                  <ItemSheet
                    item={item}
                    theme={theme}
                    aria-label={item.fields.ctaSheetLabel || 'Open dialog'}
                    analyticsName={`cosmos-landing-page-list-item-${item.fields?.analyticsId}`}
                    listItemTitleSize={listItemTitleSize}
                  />
                ) : (
                  <ItemDetails item={item} theme={theme} listItemTitleSize={listItemTitleSize} />
                )}
              </Box>
            ))}
          </Col>
          {fields.seeMoreLinkText && fields.seeMoreLinkUrl && (
            <Box className="mt-8 block">
              <Box
                href={fields.seeMoreLinkUrl}
                aria-label="See More"
                analyticsName=""
                className="w-max"
              >
                <Text
                  as="span"
                  className={cn(
                    'font-medium',
                    fields.bgColor === 'Dark'
                      ? 'text-od-content-inverse-primary underline'
                      : 'text-od-content-accent-brand no-underline',
                  )}
                >
                  {fields.seeMoreLinkText}
                </Text>
              </Box>
            </Box>
          )}
        </Box>
      </Col>
    </Container>
  );
};

const List: EntryComponent<ILpComponentList> = {
  render: RenderList,
};

export default List;
